import { FormProductOptionValue } from '@common/models/Option';
import { PRODUCT_OPTIONS_TYPE } from '@common/models/Product';
import { PRODUCT_CUSTOM_FIELD_TYPE } from '@common/models/Product';
import { CartProductType } from '@common/store/cart/slice';
import { addProductToCart, removeProductFromCart, subtractProductFromCart } from '@common/store/cart/slice';
import Stepper from '@components/Stepper';
import Close from '@images/Close.svg';
import Rupee from '@instamojo/rupee';
import classnames from 'classnames';
import React from 'react';

interface CheckoutProps {
  product: CartProductType;
  addProductToCart: typeof addProductToCart;
  subtractProductFromCart: typeof subtractProductFromCart;
  removeProductFromCart: typeof removeProductFromCart;
  cartProductQuantity: number;
  cartHasOptionProduct: boolean;
  cartHasVariant: boolean;
}

const Checkout: React.FC<CheckoutProps> = ({
  product,
  addProductToCart,
  subtractProductFromCart,
  removeProductFromCart,
  cartProductQuantity,
  cartHasVariant,
}) => {
  return (
    <div key={product.id} className="mb-17 md:mb-40">
      <div className="flex md:h-auto">
        <div className="flex">
          <div className="mr-14 relative min-h-120">
            <div className="rounded-4 overflow-hidden w-144 h-144 mb-8">
              <img
                src={product.images[0]}
                alt={product.title}
                className="w-144 h-144 max-w-144 max-h-144 object-contain"
              />
            </div>
          </div>
        </div>
        <div className="w-full relative">
          <div className="">
            <div className={classnames('w-203 md:w-278', {})}>
              <div className="mb-8 text-title text-14 leading-22 md:text-20 md:leading-26 font-normal">
                <div className="flex justify-between">
                  <div font-customisation="section-heading" color-customisation="page-heading">
                    {product.title}
                  </div>
                  <div
                    onClick={() => {
                      removeProductFromCart({ product });
                    }}
                  >
                    <img src={Close} alt="" className="min-h-10 min-w-10" />
                  </div>
                </div>
              </div>
            </div>
            {cartHasVariant ? (
              <div className=" flex-wrap">
                {product?.optionValues?.map((item, index) => {
                  return (
                    <div
                      className="text-text_variant flex items-center text-16 leading-24 font-normal mb-12 "
                      font-customisation="para-text"
                      key={item}
                    >
                      {product?.optionNames[index]} : {item}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          {product?.formProductOptions?.map((option) => {
            return (
              <div key={option.option.id} className="w-full mb-8 text-14 text-text_variant">
                <div className="font-bold">{option.option.name}</div>
                <div className="flex justify-between">
                  {option.option.type === PRODUCT_OPTIONS_TYPE.TEXT ||
                  option.option.type === PRODUCT_OPTIONS_TYPE.DATE ||
                  option.option.type === PRODUCT_OPTIONS_TYPE.DATETIME ||
                  option.option.type === PRODUCT_OPTIONS_TYPE.TIME ? (
                    <div>{option.value}</div>
                  ) : option.option.type === PRODUCT_OPTIONS_TYPE.FILE ? (
                    <div>
                      <a
                        href={option.value as string}
                        target="_blank"
                        rel="noreferrer"
                        download={option.value}
                        className="text-text_variant"
                      >
                        File Uploaded
                      </a>
                    </div>
                  ) : option.option.type === PRODUCT_OPTIONS_TYPE.RADIO ? (
                    <div>{(option.value as FormProductOptionValue)?.text}</div>
                  ) : option.option.type === PRODUCT_OPTIONS_TYPE.CHECKBOX ? (
                    <div>
                      <div>{(option.value as FormProductOptionValue[]).map((v) => v.text).join('|')}</div>
                    </div>
                  ) : null}
                  {option.option.charges > 0 && (
                    <div className="flex gap-10">
                      Charges :
                      <div className="text-theme">
                        <Rupee value={option.option.charges} retainDecimalPlaces />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {product?.formCustomFields?.map((option) => {
            return (
              <div key={option?.id} className="w-full mb-8 text-14 text-text_variant">
                <div className="font-bold">{option?.title}</div>
                <div className="flex justify-between">
                  {option?.field_type === PRODUCT_CUSTOM_FIELD_TYPE.TEXT ||
                  option?.field_type === PRODUCT_CUSTOM_FIELD_TYPE.DATE ||
                  option?.field_type === PRODUCT_CUSTOM_FIELD_TYPE.DATETIME ||
                  option?.field_type === PRODUCT_CUSTOM_FIELD_TYPE.TIME ? (
                    <div>{option.values[0]}</div>
                  ) : option?.field_type === PRODUCT_CUSTOM_FIELD_TYPE.FILE ? (
                    <div>
                      <a
                        href={option.public_urls[option.values[0]] as string}
                        target="_blank"
                        rel="noreferrer"
                        download={option.public_urls[option.values[0]]}
                        className="text-black_variant_1"
                      >
                        File Uploaded
                      </a>
                    </div>
                  ) : option?.field_type === PRODUCT_CUSTOM_FIELD_TYPE.RADIO ? (
                    <div>{(option?.values as FormProductOptionValue).join(', ')}</div>
                  ) : option?.field_type === PRODUCT_CUSTOM_FIELD_TYPE.CHECKBOX ? (
                    <div>
                      <div>{(option.values as FormProductOptionValue[]).map((v) => v).join('|')}</div>
                    </div>
                  ) : option.field_type === PRODUCT_CUSTOM_FIELD_TYPE.MULTI_FILE ? (
                    <div>
                      {(option?.values as FormProductOptionValue[]).map((v, index) => {
                        return (
                          <div key={index}>
                            <a
                              href={option?.public_urls[v] as string}
                              target="_blank"
                              rel="noreferrer"
                              download={option?.public_urls[v]}
                              className="text-black_variant_1"
                            >
                              File Uploaded {index + 1}
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {option?.charges > 0 && (
                    <div className="flex gap-10">
                      Charges :
                      <div className="text-theme">
                        <Rupee value={option?.charges} retainDecimalPlaces />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div className="flex justify-between relative items-center mb-12">
            <div>
              <Stepper
                className=""
                isSmallerVariant
                product={product}
                subtractProductFromCart={subtractProductFromCart}
                addProductToCart={addProductToCart}
                quantity={product.cartQuantity}
                cartProductQuantity={cartProductQuantity}
              />
            </div>
            <div className="" font-customisation="para-text" color-customisation="page-body-text">
              <Rupee
                className="text-title text-14 leading-22 md:text-16 md:leading-24"
                value={product.cartQuantity * product.finalPrice}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
